"use strict"

import UserConversionJourneyDialogTemplate from "./user-conversion-journey-dialog.html"
import AnalyticsService from "../../services/analytics/analytics.service"
import StaticContentService from "../../services/static/static-content.service"

/**
 * Controller for dynamic popup dialogs with conversion overlays
 */
export default class UserConversionJourneyController {
  /**
   * @ngInject
   * @param {AnalyticsService} AnalyticsService
   * @param {StaticContentService} StaticContentService
   */
  constructor(
    $mdDialog,
    $log,
    $window,
    AnalyticsService,
    StaticContentService,
    user,
    userUsage,
  ) {
    // Angular services
    this.$mdDialog = $mdDialog
    this.$log = $log
    this.$window = $window

    // Services
    /** @type {AnalyticsService} */
    this._analyticsService = AnalyticsService
    /** @type {StaticContentService} */
    this._staticContentService = StaticContentService

    // Data models
    this._user = user
    this._userUsage = userUsage

    // Component state
    this.dynamicPopupData = undefined
    this.isLoading = true

    // Initialize conversion popup
    this._initializeConversionPopup()
  }

  /**
   * Initialize conversion popup functionality
   */
  _initializeConversionPopup() {
    try {
      this._handlePopupConfig(this._userUsage)
    } catch (error) {
      this.$log.error("Failed to initialize conversion popup:", error)
    } finally {
      this.isLoading = false
    }
  }

  /**
   * Handle popup configuration
   * @param {Object} config - Popup configuration
   */
  _handlePopupConfig(config) {
    if (config) {
      this.dynamicPopupData = config
      this._trackPopupShown()
    }
  }

  /**
   * Track popup shown event
   */
  _trackPopupShown() {
    this._analyticsService.sendEvent({
      eventTag: `${this.dynamicPopupData.metric}_shown`,
      properties: {
        user: this._user.email,
        usage: this._userUsage,
      },
    })
  }

  /**
   * Handle conversion overlay popup CTA actions
   * @param {string} action - Action to perform
   */
  openConversionOverlayPopupCta(action) {
    this._trackCtaClick(action)
    return this._handleExternalLinkAction(action)
  }

  /**
   * Track CTA click event
   * @param {string} action - Action performed
   * @private
   */
  _trackCtaClick(action) {
    this._analyticsService.sendEvent({
      eventTag: `${this.dynamicPopupData.metric}_clicked`,
      properties: {
        userEmail: this._user.email,
        usage: this._userUsage,
        action: action,
      },
    })
  }

  /**
   * Handle external link action
   * @param {string} url - URL to open
   * @returns {Promise}
   * @private
   */
  _handleExternalLinkAction(url) {
    try {
      if (typeof url === "string" && url.trim()) {
        this.$window.open(url, "_blank", "noreferrer")
      }
      return Promise.resolve()
    } catch (error) {
      this.$log.error("Failed to open external link:", error)
      return Promise.reject(error)
    }
  }

  /**
   * @returns {string} Progress label with percentage
   */
  get progressLabelDays() {
    return `${this.dynamicPopupData.progressPercent}% of of this month's student pages used`
  }

  /**
   * Get progress bar style based on progress percentage
   * @returns {Object} Style object for progress bar
   */
  getProgressBarStyle() {
    const progress = this.dynamicPopupData.progressPercent
    let barColor

    if (progress < 50) {
      barColor = "#25C582"
    } else if (progress >= 50 && progress < 75) {
      barColor = "#FFA500"
    } else if (progress >= 75 && progress <= 85) {
      barColor = "#FF6B6B"
    } else {
      barColor = "#F1271D"
    }

    return {
      "--md-bar-background": barColor,
    }
  }

  goToUsageUpgradeFaq() {
    this._staticContentService.goToUsageUpgradeFaq()
  }

  /**
   * Static method to show the dialog
   * @param {Object} $mdDialog - Angular Material dialog service
   * @param {Object} user - User object
   * @param {Object} userUsage - User usage data
   * @returns {Promise} Dialog promise
   */
  static show($mdDialog, user, userUsage, onRemoving) {
    return $mdDialog.show({
      controller: UserConversionJourneyController,
      template: UserConversionJourneyDialogTemplate,
      controllerAs: "ctrl",
      clickOutsideToClose: true,
      escapeToClose: true,
      onRemoving: onRemoving,
      locals: {
        user,
        userUsage,
      },
    })
  }

  /**
   * Cancel and close the dialog
   */
  cancel() {
    this.$mdDialog.cancel()
  }
}
